import { useMemo } from "react";
import {
  UseMutationResult,
  useMutation,
  useQuery,
  useQueryClient,
} from "react-query";
import {
  createPortfolioViewerAccess,
  deletePortfolioViewerAccess,
  getPortfolioViewerAccess,
  updatePortfolioViewerAccess,
} from "apps/project-iq/api";
import {
  IPortfolioViewerAccess,
  IPortfolioViewerAccessInput,
} from "apps/project-iq/models/IPortfolioViewerAccess";

export const PORTFOLIO_VIEWER_ACCESS_QUERY_KEY = "portfolioViewerAccess";

export interface IUsePortfolioViewerAccessResult {
  portfolioViewerAccessList: IPortfolioViewerAccess[];
  isLoading: boolean;
  isError: boolean;
  createMutation: UseMutationResult<
    IPortfolioViewerAccess,
    Error,
    IPortfolioViewerAccessInput
  >;
  updateMutation: UseMutationResult<
    IPortfolioViewerAccess,
    Error,
    IPortfolioViewerAccess
  >;
  deleteMutation: UseMutationResult<unknown, Error, string>;
}

export const usePortfolioViewerAccess = (): IUsePortfolioViewerAccessResult => {
  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useQuery<
    IPortfolioViewerAccess[],
    Error
  >({
    queryKey: [PORTFOLIO_VIEWER_ACCESS_QUERY_KEY],
    queryFn: () => getPortfolioViewerAccess(),
  });

  const portfolioViewerAccessList = useMemo(() => data ?? [], [data]);

  const createMutation = useMutation<
    IPortfolioViewerAccess,
    Error,
    IPortfolioViewerAccessInput
  >(
    (input: IPortfolioViewerAccessInput) => createPortfolioViewerAccess(input),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([PORTFOLIO_VIEWER_ACCESS_QUERY_KEY]);
      },
    },
  );

  const updateMutation = useMutation<
    IPortfolioViewerAccess,
    Error,
    IPortfolioViewerAccess
  >(
    ({ id, user, portfolioIds }: IPortfolioViewerAccess) =>
      updatePortfolioViewerAccess(id, { user, portfolioIds }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([PORTFOLIO_VIEWER_ACCESS_QUERY_KEY]);
      },
    },
  );

  const deleteMutation = useMutation<unknown, Error, string>(
    (id: string) => deletePortfolioViewerAccess(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([PORTFOLIO_VIEWER_ACCESS_QUERY_KEY]);
      },
    },
  );

  return {
    portfolioViewerAccessList,
    isLoading,
    isError,
    createMutation,
    updateMutation,
    deleteMutation,
  };
};
